import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { formatDistanceToNow, parseJSON, format } from 'date-fns';
import { useAuditLog } from '../../data/useInternalAdmin';
import AQDataGrid from '../AQDataGrid';

const ENTRY_TYPE_COLORS: Record<
  string,
  'primary' | 'secondary' | 'warning' | 'default'
> = {
  assumed_role: 'primary',
  auth_log: 'secondary',
  admin_action: 'warning',
};

const ACTION_LABELS: Record<string, string> = {
  invite_user: 'Invited user',
  update_user_role: 'Changed role',
  remove_user: 'Removed user',
  create_account: 'Created account',
  end_assumed_role: 'Deactivated role',
  deactivate_account: 'Deactivated account',
  reactivate_account: 'Reactivated account',
  add_entitlement: 'Added entitlement',
  remove_entitlement: 'Removed entitlement',
  update_entitlement: 'Updated entitlement',
};

const summarizeDetails = (
  entryType: string,
  details: Record<string, any>,
): string => {
  if (entryType === 'assumed_role') {
    const action = details['ended_at'] ? 'Ended' : 'Assumed';
    return `${action} ${details['assumed_role']} role — ${details['reason'] || ''}`;
  }
  if (entryType === 'auth_log') {
    return `${details['auth_type'] || 'Auth event'}${details['ip'] ? ` from ${details['ip']}` : ''}`;
  }
  if (entryType === 'admin_action') {
    const label = ACTION_LABELS[details['action']] || details['action'];
    if (details['email'])
      return `${label}: ${details['email']} (${details['role'] || details['new_role'] || ''})`;
    if (details['name']) return `${label}: ${details['name']}`;
    return label;
  }
  return JSON.stringify(details);
};

const InternalAuditLog: React.FC = () => {
  const auditQuery = useAuditLog();
  const entries = auditQuery.data?.data || [];

  const rows = React.useMemo(
    () =>
      entries.map((entry) => ({
        id: entry.id,
        timestamp: entry.attributes.timestamp,
        entry_type: entry.attributes.entry_type,
        user_email: entry.attributes.user_email,
        account_name: entry.attributes.account_name || '\u2014',
        details: entry.attributes.details,
        _summary: summarizeDetails(
          entry.attributes.entry_type,
          entry.attributes.details,
        ),
      })),
    [entries],
  );

  const colDefs = React.useMemo(
    () => [
      {
        field: 'timestamp',
        headerName: 'Time',
        width: 180,
        sortable: true,
        filterable: false,
        renderCell: (params: any) => (
          <Tooltip
            title={format(parseJSON(params.value), 'PPpp')}
            placement="top"
          >
            <Typography variant="body2" color="text.secondary">
              {formatDistanceToNow(parseJSON(params.value), {
                addSuffix: true,
              })}
            </Typography>
          </Tooltip>
        ),
      },
      {
        field: 'entry_type',
        headerName: 'Type',
        width: 150,
        sortable: true,
        filterable: true,
        renderCell: (params: any) => (
          <Chip
            label={params.value.replace('_', ' ')}
            size="small"
            color={ENTRY_TYPE_COLORS[params.value] || 'default'}
            variant="outlined"
          />
        ),
      },
      {
        field: 'user_email',
        headerName: 'User',
        flex: 1,
        minWidth: 200,
        sortable: true,
        filterable: true,
      },
      {
        field: 'account_name',
        headerName: 'Account',
        width: 180,
        sortable: true,
        filterable: true,
      },
      {
        field: '_summary',
        headerName: 'Details',
        flex: 2,
        minWidth: 300,
        sortable: false,
        filterable: false,
        renderCell: (params: any) => (
          <Tooltip
            title={
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {JSON.stringify(params.row.details, null, 2)}
              </pre>
            }
            placement="top-start"
          >
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        ),
      },
    ],
    [],
  );

  const gridProps = React.useMemo(
    () => ({
      getEstimatedRowHeight: () => 52,
      getRowHeight: () => 'auto' as const,
      sortingMode: 'client' as const,
      filterMode: 'client' as const,
      paginationMode: 'client' as const,
      disableColumnMenu: true,
      disableRowSelectionOnClick: true,
    }),
    [],
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          mb: 3,
          maxWidth: 1200,
          margin: '0 auto 24px',
        }}
      >
        <Typography variant="h5">Audit Log</Typography>
      </Box>

      <AQDataGrid
        id="internal-audit-log"
        rows={rows}
        colDefs={colDefs}
        loading={auditQuery.isLoading}
        gridProps={gridProps}
        customErrorElement={
          auditQuery.isError ? (
            <Typography color="error" sx={{ p: 2, textAlign: 'center' }}>
              Failed to load audit log
            </Typography>
          ) : undefined
        }
        sx={{
          '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
            py: '12px',
          },
        }}
      />
    </Box>
  );
};

export default InternalAuditLog;
